import { Capacitor, registerPlugin } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

/**
 * Instagram's own hand-off, in the native shells only (InstagramStoryPlugin.swift /
 * InstagramStoryPlugin.java). `source_application` wants a Facebook app id; Instagram has
 * historically taken anything non-empty and the plugins fall back to the bundle id, so set
 * REACT_APP_FB_APP_ID if a real one is ever registered.
 */
const InstagramStory = registerPlugin('InstagramStory');
const FB_APP_ID = process.env.REACT_APP_FB_APP_ID || '';

/**
 * Sharing an image, on the web and inside the native shell.
 *
 * `navigator.share` is the wrong tool in a Capacitor WebView. On Android it
 * isn't implemented at all (the Web Share API is gated to Chrome proper, not
 * the system WebView). On iOS it EXISTS on `navigator`, which is worse — the
 * feature test passes, the call resolves, and no sheet ever appears, because
 * presenting one is the host app's job and a plain WKWebView never does it.
 * Either way the button looked dead, and the old fallback couldn't save it: an
 * `<a download>` click does nothing in a WebView either, so both paths ended in
 * silence.
 *
 * Natively the OS sheet has to be asked for directly, and it takes a file on
 * disk rather than a Blob — so the image is staged in the cache directory
 * first, and cleaned up afterwards.
 */

export const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

/** Blob → bare base64 (no data: prefix), which is what Filesystem.writeFile wants. */
function toBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Could not read image'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Open the native share sheet with `blob` attached.
 * Returns true if the sheet was presented, false if this isn't a native build.
 * Throws only on a real failure — a dismissed sheet is a success.
 */
export async function shareImageNative({ blob, fileName, title, text, url }) {
  if (!isNative()) return false;

  const path = `share-${Date.now()}-${fileName}`;
  let uri;
  try {
    const written = await Filesystem.writeFile({
      path,
      data: await toBase64(blob),
      directory: Directory.Cache,
    });
    uri = written.uri;

    // `files` wants a file:// URI. Text and url alongside it are deliberately
    // optional: some targets (Instagram Stories among them) drop the image when
    // a URL rides along, so callers that care about the picture send only it.
    await Share.share({
      ...(title ? { title } : {}),
      ...(text ? { text } : {}),
      ...(url ? { url } : {}),
      files: [uri],
      dialogTitle: title || 'Share',
    });
  } finally {
    // Best-effort: the cache directory is the OS's to reclaim, and a failed
    // delete must never turn a completed share into an error.
    if (uri) { try { await Filesystem.deleteFile({ path, directory: Directory.Cache }); } catch { /* ignore */ } }
  }
  return true;
}

/** Blob → bare base64, for the native side. */
const base64Of = (blob) => toBase64(blob);

/**
 * Put the card straight into Instagram's story composer.
 *
 * The OS sheet does work and is still what MESSAGE opens — a DM, another app, anywhere — but
 * Instagram sits wherever the system ranks it there, which on iOS meant More and a scroll to
 * the bottom. This is Instagram's own route: the media goes over, the composer opens with it
 * already on the canvas.
 *
 * Returns false when it isn't on offer (the web, or Instagram not installed), so the caller
 * can fall back to the sheet rather than leaving someone with a button that did nothing. The
 * LINK still goes on by hand: Instagram only honours a contentURL from apps it has approved,
 * which is what the sheet's step 1 is for.
 */
export async function shareToInstagramStory({ blob, video = false }) {
  if (!isNative() || !blob) return false;
  try {
    const { available } = await InstagramStory.available();
    if (!available) return false;
    await InstagramStory.share({ data: await base64Of(blob), video, appId: FB_APP_ID });
    return true;
  } catch {
    return false;   // whatever went wrong, the share sheet is still there
  }
}

/**
 * Share an image wherever the app happens to be running.
 *
 * Native first, then the Web Share API, then a download. The last of those is a
 * real fallback on the web and a dead end in a WebView, which is why native is
 * checked before either.
 */
export async function shareImage({ blob, fileName, imageUrl, title, text, url }) {
  if (isNative()) {
    try {
      await shareImageNative({ blob, fileName, title, text, url });
      return 'native';
    } catch (err) {
      // A user dismissing the sheet surfaces as an error on both platforms.
      if (/cancel|abort|dismiss/i.test(err?.message || '')) return 'dismissed';
      throw err;
    }
  }

  const file = new File([blob], fileName, { type: blob.type || 'image/png' });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], ...(title ? { title } : {}), ...(text ? { text } : {}) });
      return 'web-share';
    } catch (err) {
      if (err?.name === 'AbortError') return 'dismissed';
      throw err;
    }
  }

  // The last resort is a download, and it has to be of something: `imageUrl` is optional (the
  // Portal share sheet only ever has a freshly rendered Blob), so fall back to the Blob itself
  // rather than clicking a link with `href="undefined"`, which is what used to happen — the
  // button did nothing at all on every desktop browser that can't share a file.
  const href = imageUrl || URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = fileName;
  link.href = href;
  link.click();
  if (!imageUrl) setTimeout(() => URL.revokeObjectURL(href), 4000);
  return 'download';
}

/**
 * Share a link (no image).
 *
 * Same trap as the image path, and the one place it bites hardest: on iOS
 * `navigator.share` exists, so a `navigator.share && ...` test sends the call
 * into a void. On Android the test fails and the caller falls through to its own
 * dialog, which at least does something — so this bug looked platform-specific
 * when it was really the same bug wearing two masks.
 *
 * Returns true when a sheet was presented (or dismissed), false when there's
 * nothing native or web to present, so the caller can open its own dialog.
 */
export async function shareLink({ title, text, url }) {
  if (isNative()) {
    try {
      await Share.share({
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
        url,
        dialogTitle: title || 'Share',
      });
    } catch (err) {
      if (!/cancel|abort|dismiss/i.test(err?.message || '')) throw err;
    }
    return true;
  }

  if (navigator.share) {
    try { await navigator.share({ ...(title ? { title } : {}), ...(text ? { text } : {}), url }); }
    catch (err) { if (err?.name !== 'AbortError') throw err; }
    return true;
  }
  return false;
}

/**
 * Save an image to the device.
 *
 * On the web that's a download. Natively there is no download — and no
 * permission-free way to write to the photo library either — so this opens the
 * OS sheet, which is where "Save Image" actually lives on both platforms. That
 * makes it the same gesture as sharing, which is why a surface offering both
 * should show only the share button natively (see ShareSheet).
 */
export async function saveImage({ blob, fileName, imageUrl }) {
  if (isNative()) {
    await shareImageNative({ blob, fileName, title: 'Save image' });
    return 'native';
  }
  const href = imageUrl || URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = fileName;
  link.href = href;
  link.click();
  if (!imageUrl) setTimeout(() => URL.revokeObjectURL(href), 4000);
  return 'download';
}
